import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { PeriodFilter } from './useExecutiveData';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface AccessKPIs {
  usersAccessedInPeriod: number;
  totalSessionsInPeriod: number;
  sessionsToday: number;
  sessionsLast7d: number;
  sessionsLast30d: number;
  usersInactiveOver7d: number;
  usersInactiveOver15d: number;
  usersInactiveOver30d: number;
  newUsersFirstAccess: number;
  recurringUsers: number;
  avgSessionsPerActiveUser: number;
  totalUsers: number;
}

export interface DistItem {
  label: string;
  count: number;
  pct: number;
}

export interface AccessDeviceData {
  deviceDist: DistItem[];
  browserDist: DistItem[];
  osDist: DistItem[];
}

export interface DailyAccessPoint {
  date: string;
  sessions: number;
  uniqueUsers: number;
}

export interface HourlyPoint {
  hour: number;
  count: number;
}

export type ActivityStatus = 'active' | 'inactive_7d' | 'inactive_15d' | 'inactive_30d' | 'never';

export interface UserAccessRow {
  id: string;
  full_name: string;
  email: string;
  plan_name: string;
  plan_id: string | null;
  last_access: string | null;
  sessions_in_period: number;
  total_sessions: number;
  device_type: string | null;
  browser_name: string | null;
  os_name: string | null;
  activity_status: ActivityStatus;
  days_since_access: number | null;
}

export interface AccessAlert {
  type: 'danger' | 'warning' | 'info' | 'success';
  icon: string;
  title: string;
  description: string;
  value?: string;
}

export interface LoginLogEntry {
  session_id: string;
  user_id: string;
  full_name: string;
  email: string;
  plan_name: string;
  login_at: string;
  last_active_at: string | null;
  device_type: string | null;
  browser_name: string | null;
  os_name: string | null;
  ip_address: string | null;
  session_label: string | null;
  is_admin: boolean;
  /** true = entry derived from profiles.last_seen_at (no user_sessions record) */
  is_synthetic: boolean;
}

export interface AccessData {
  kpis: AccessKPIs;
  devices: AccessDeviceData;
  dailyTrend: DailyAccessPoint[];
  hourlyDist: HourlyPoint[];
  userTable: UserAccessRow[];
  alerts: AccessAlert[];
  plans: { id: string; name: string }[];
  loginLog: LoginLogEntry[];
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const ADMIN_ROLES = ['master_admin', 'admin', 'support', 'finance'];

const getPeriodStart = (period: PeriodFilter): Date | null => {
  if (period === 'all') return null;
  const d = new Date();
  if (period === '7d') d.setDate(d.getDate() - 7);
  else if (period === '30d') d.setDate(d.getDate() - 30);
  else if (period === '90d') d.setDate(d.getDate() - 90);
  else if (period === '365d') d.setFullYear(d.getFullYear() - 1);
  return d;
};

const mkDist = (values: string[]): DistItem[] => {
  const total = values.length || 1;
  const counts: Record<string, number> = {};
  values.forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
  return Object.entries(counts)
    .map(([label, count]) => ({ label, count, pct: Math.round((count / total) * 100) }))
    .sort((a, b) => b.count - a.count);
};

const getActivityStatus = (lastAccess: string | null): { status: ActivityStatus; days: number | null } => {
  if (!lastAccess) return { status: 'never', days: null };
  // Use exact milliseconds (no Math.floor) to match timestamp-based KPI counters:
  // usersInactiveOver7d uses `la < last7Ts` (= now - 7*86400000)
  const ms = Date.now() - new Date(lastAccess).getTime();
  const daysExact = ms / 86400000;
  const daysFloor = Math.floor(daysExact);
  if (daysExact <= 7)  return { status: 'active',       days: daysFloor };
  if (daysExact <= 15) return { status: 'inactive_7d',  days: daysFloor };
  if (daysExact <= 30) return { status: 'inactive_15d', days: daysFloor };
  return { status: 'inactive_30d', days: daysFloor };
};

const pct = (part: number, total: number) => (total > 0 ? Math.round((part / total) * 100) : 0);

// LGPD: mask last two octets of IPv4 (e.g. 192.168.1.5 → 192.168.*.*) 
const maskIp = (ip: string | null): string | null => {
  if (!ip) return null;
  const parts = ip.split('.');
  if (parts.length === 4) return `${parts[0]}.${parts[1]}.*.*`;
  // IPv6 or unknown — mask end
  if (ip.includes(':')) return ip.slice(0, ip.lastIndexOf(':') + 1) + '****';
  return ip;
};

// ─── Default state ──────────────────────────────────────────────────────────

const DEFAULT_DATA: AccessData = {
  kpis: {
    usersAccessedInPeriod: 0, totalSessionsInPeriod: 0, sessionsToday: 0,
    sessionsLast7d: 0, sessionsLast30d: 0, usersInactiveOver7d: 0,
    usersInactiveOver15d: 0, usersInactiveOver30d: 0, newUsersFirstAccess: 0,
    recurringUsers: 0, avgSessionsPerActiveUser: 0, totalUsers: 0,
  },
  devices: { deviceDist: [], browserDist: [], osDist: [] },
  dailyTrend: [],
  hourlyDist: [],
  userTable: [],
  alerts: [],
  plans: [],
  loginLog: [],
};

// ─── Hook ──────────────────────────────────────────────────────────────────

export const useAccessLogs = (period: PeriodFilter) => {
  const { user } = useAuth();
  const [data, setData] = useState<AccessData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const periodStart = getPeriodStart(period);
      const now = new Date();
      const today = now.toISOString().slice(0, 10);
      const todayStart = `${today}T00:00:00.000Z`;
      const last7Ts = new Date(now.getTime() - 7 * 86400000).toISOString();
      const last15Ts = new Date(now.getTime() - 15 * 86400000).toISOString();
      const last30Ts = new Date(now.getTime() - 30 * 86400000).toISOString();

      // ── Fetch admin IDs to exclude ─────────────────────────────────────
      const { data: adminRoles } = await supabase
        .from('user_roles').select('user_id').in('role', ADMIN_ROLES);
      const adminIds = new Set((adminRoles || []).map((r: any) => r.user_id));

      // ── Fetch plans ────────────────────────────────────────────────────
      const { data: plansData } = await supabase.from('plans').select('id, name');
      const plans = (plansData || []).map((p: any) => ({ id: p.id, name: p.name }));

      // ── Fetch profiles ─────────────────────────────────────────────────
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, full_name, name, email, last_seen_at, created_at')
        .limit(10000);
      const allProfiles = (profilesData || []).filter((p: any) => !adminIds.has(p.id));
      const allUserIds = new Set(allProfiles.map((p: any) => p.id));
      // For the login audit log we want ALL users (including admins) — admins log in too
      const allProfilesForLog = profilesData || [];
      const allProfilesByIdForLog = new Map(allProfilesForLog.map((p: any) => [p.id, p]));

      // ── Fetch subscriptions for plan mapping ───────────────────────────
      const { data: subsData } = await supabase
        .from('subscriptions')
        .select('user_id, plan_id, status, created_at')
        .limit(10000);

      const planByUser = new Map<string, { plan_id: string; plan_name: string }>();
      (subsData || [])
        .sort((a: any, b: any) => {
          const pa = a.status === 'active' ? 3 : a.status === 'trial' ? 2 : 1;
          const pb = b.status === 'active' ? 3 : b.status === 'trial' ? 2 : 1;
          return pb - pa;
        })
        .forEach((s: any) => {
          if (!planByUser.has(s.user_id) && allUserIds.has(s.user_id)) {
            const plan = plans.find((p) => p.id === s.plan_id);
            planByUser.set(s.user_id, {
              plan_id: s.plan_id,
              plan_name: plan?.name ?? 'Sem plano',
            });
          }
        });

      // ── Fetch all sessions (non-revoked) ───────────────────────────────
      const { data: sessionsRaw } = await supabase
        .from('user_sessions')
        .select('id, user_id, device_type, browser_name, os_name, ip_address, session_label, last_active_at, created_at')
        .eq('is_revoked', false)
        .order('created_at', { ascending: false })
        .limit(50000);
      // allSessions: non-admin only (used for KPIs / user analytics)
      const allSessions = (sessionsRaw || []).filter((s: any) => allUserIds.has(s.user_id));
      // allSessionsForLog: ALL sessions including admins (used for login audit log)
      const allSessionsForLog = sessionsRaw || [];
      // Pre-compute set of user_ids with ANY session (used for fallback detection)
      const usersWithAnySession = new Set(allSessionsForLog.map((s: any) => s.user_id));

      // ── Period sessions ────────────────────────────────────────────────
      const periodSessions = periodStart
        ? allSessions.filter((s: any) =>
            new Date(s.last_active_at || s.created_at) >= periodStart
          )
        : allSessions;
      // Period sessions for the login log (includes admins)
      const periodSessionsForLog = periodStart
        ? allSessionsForLog.filter((s: any) =>
            new Date(s.last_active_at || s.created_at) >= periodStart
          )
        : allSessionsForLog;

      const todaySessions = allSessions.filter((s: any) => (s.last_active_at || s.created_at) >= todayStart);
      const last7dSessions = allSessions.filter((s: any) => (s.last_active_at || s.created_at) >= last7Ts);
      const last30dSessions = allSessions.filter((s: any) => (s.last_active_at || s.created_at) >= last30Ts);

      const usersInPeriod = new Set(periodSessions.map((s: any) => s.user_id));
      // Also count users with last_seen_at in period but no session records at all
      allProfiles.forEach((p: any) => {
        if (usersWithAnySession.has(p.id) || !p.last_seen_at) return;
        const isInPeriod = !periodStart || new Date(p.last_seen_at) >= periodStart;
        if (isInPeriod) usersInPeriod.add(p.id);
      });

      // ── Last access per user ───────────────────────────────────────────
      const lastAccessByUser = new Map<string, string>();
      allSessions.forEach((s: any) => {
        const ts = s.last_active_at || s.created_at;
        const existing = lastAccessByUser.get(s.user_id);
        if (!existing || ts > existing) lastAccessByUser.set(s.user_id, ts);
      });
      // Fallback: profiles.last_seen_at
      allProfiles.forEach((p: any) => {
        if (!lastAccessByUser.has(p.id) && p.last_seen_at) {
          lastAccessByUser.set(p.id, p.last_seen_at);
        }
      });

      // ── Most recent session per user (for device/browser info) ─────────
      const lastSessionByUser = new Map<string, any>();
      allSessions.forEach((s: any) => {
        const ts = s.last_active_at || s.created_at;
        const existing = lastSessionByUser.get(s.user_id);
        const exTs = existing ? (existing.last_active_at || existing.created_at) : '';
        if (!existing || ts > exTs) lastSessionByUser.set(s.user_id, s);
      });

      // ── Inactivity counts ──────────────────────────────────────────────
      const usersInactiveOver7d = allProfiles.filter((p: any) => {
        const la = lastAccessByUser.get(p.id);
        return !la || la < last7Ts;
      }).length;
      const usersInactiveOver15d = allProfiles.filter((p: any) => {
        const la = lastAccessByUser.get(p.id);
        return !la || la < last15Ts;
      }).length;
      const usersInactiveOver30d = allProfiles.filter((p: any) => {
        const la = lastAccessByUser.get(p.id);
        return !la || la < last30Ts;
      }).length;

      // ── First session per user ─────────────────────────────────────────
      const firstSessionByUser = new Map<string, string>();
      allSessions.forEach((s: any) => {
        const ts = s.created_at;
        const existing = firstSessionByUser.get(s.user_id);
        if (!existing || ts < existing) firstSessionByUser.set(s.user_id, ts);
      });

      const newUsersFirstAccess = periodStart
        ? allProfiles.filter((p: any) => {
            const first = firstSessionByUser.get(p.id);
            return first && new Date(first) >= periodStart;
          }).length
        : allProfiles.length;

      const recurringUsers = periodStart
        ? allProfiles.filter((p: any) => {
            if (!usersInPeriod.has(p.id)) return false;
            const first = firstSessionByUser.get(p.id);
            return first && new Date(first) < periodStart;
          }).length
        : 0;

      // ── Sessions per user (period) ────────────────────────────────────
      const sessionsPerUser: Record<string, number> = {};
      periodSessions.forEach((s: any) => {
        sessionsPerUser[s.user_id] = (sessionsPerUser[s.user_id] || 0) + 1;
      });
      const totalSessionsPerUser: Record<string, number> = {};
      allSessions.forEach((s: any) => {
        totalSessionsPerUser[s.user_id] = (totalSessionsPerUser[s.user_id] || 0) + 1;
      });

      const avgSessionsPerActiveUser =
        usersInPeriod.size > 0
          ? Math.round((periodSessions.length / usersInPeriod.size) * 10) / 10
          : 0;

      const kpis: AccessKPIs = {
        usersAccessedInPeriod: usersInPeriod.size,
        totalSessionsInPeriod: periodSessions.length,
        sessionsToday: todaySessions.length,
        sessionsLast7d: last7dSessions.length,
        sessionsLast30d: last30dSessions.length,
        usersInactiveOver7d,
        usersInactiveOver15d,
        usersInactiveOver30d,
        newUsersFirstAccess,
        recurringUsers,
        avgSessionsPerActiveUser,
        totalUsers: allProfiles.length,
      };

      // ── Device / Browser / OS distribution ────────────────────────────
      const deviceLabels: Record<string, string> = {
        desktop: 'Desktop', mobile: 'Mobile', tablet: 'Tablet',
      };
      const deviceDist = mkDist(
        allSessions.map((s: any) => deviceLabels[s.device_type] || s.device_type || 'Desconhecido')
      );
      const browserDist = mkDist(
        allSessions.map((s: any) => s.browser_name || 'Desconhecido')
      );
      const osDist = mkDist(
        allSessions.map((s: any) => s.os_name || 'Desconhecido')
      );

      // ── Daily trend (last 30 days) ─────────────────────────────────────
      const dailyTrend: DailyAccessPoint[] = [];
      for (let i = 29; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 86400000);
        const dateStr = d.toISOString().slice(0, 10);
        const dayStart = `${dateStr}T00:00:00.000Z`;
        const dayEnd = `${dateStr}T23:59:59.999Z`;
        const daySessions = allSessions.filter((s: any) => {
          const ts = s.last_active_at || s.created_at;
          return ts >= dayStart && ts <= dayEnd;
        });
        dailyTrend.push({
          date: dateStr,
          sessions: daySessions.length,
          uniqueUsers: new Set(daySessions.map((s: any) => s.user_id)).size,
        });
      }

      // ── Hourly distribution ───────────────────────────────────────────
      const hourCounts: number[] = new Array(24).fill(0);
      allSessions.forEach((s: any) => {
        const ts = s.last_active_at || s.created_at;
        if (ts) hourCounts[new Date(ts).getHours()]++;
      });
      const hourlyDist: HourlyPoint[] = hourCounts.map((count, hour) => ({ hour, count }));

      // ── User table ─────────────────────────────────────────────────────
      const userTable: UserAccessRow[] = allProfiles.map((p: any) => {
        const lastAccess = lastAccessByUser.get(p.id) ?? null;
        const { status: activity_status, days: days_since_access } = getActivityStatus(lastAccess);
        const lastSess = lastSessionByUser.get(p.id);
        const planInfo = planByUser.get(p.id);
        return {
          id: p.id,
          full_name: p.full_name || p.name || 'Sem nome',
          email: p.email || '',
          plan_name: planInfo?.plan_name ?? 'Sem plano',
          plan_id: planInfo?.plan_id ?? null,
          last_access: lastAccess,
          sessions_in_period: sessionsPerUser[p.id] || 0,
          total_sessions: totalSessionsPerUser[p.id] || 0,
          device_type: lastSess?.device_type ?? null,
          browser_name: lastSess?.browser_name ?? null,
          os_name: lastSess?.os_name ?? null,
          activity_status,
          days_since_access,
        };
      });

      // ── Login log (individual sessions, newest first) ──────────────────
      // Uses periodSessionsForLog which includes admin sessions too (security audit)
      const loginLog: LoginLogEntry[] = periodSessionsForLog
        .slice()
        .sort((a: any, b: any) => {
          const ta = a.created_at ?? '';
          const tb = b.created_at ?? '';
          return tb.localeCompare(ta);
        })
        .map((s: any) => {
          const profile = allProfilesByIdForLog.get(s.user_id);
          const planInfo = planByUser.get(s.user_id);
          return {
            session_id: s.id,
            user_id: s.user_id,
            full_name: profile?.full_name || profile?.name || 'Sem nome',
            email: profile?.email || '',
            plan_name: planInfo?.plan_name ?? (adminIds.has(s.user_id) ? 'Admin' : 'Sem plano'),
            login_at: s.created_at,
            last_active_at: s.last_active_at ?? null,
            device_type: s.device_type ?? null,
            browser_name: s.browser_name ?? null,
            os_name: s.os_name ?? null,
            ip_address: maskIp(s.ip_address ?? null),
            session_label: s.session_label ?? null,
            is_admin: adminIds.has(s.user_id),
            is_synthetic: false,
          };
        });

      // ── Synthetic entries: users with last_seen_at but no session records ──
      // These users genuinely accessed the platform but user_sessions was not
      // created (e.g. accessed before session tracking was stable, or on devices
      // where localStorage was cleared). We add one synthetic entry per user
      // using profiles.last_seen_at as the best available access timestamp.
      allProfilesForLog.forEach((p: any) => {
        if (usersWithAnySession.has(p.id) || !p.last_seen_at) return;
        const isInPeriod = !periodStart || new Date(p.last_seen_at) >= periodStart;
        if (!isInPeriod) return;
        const planInfo = planByUser.get(p.id);
        loginLog.push({
          session_id: `synthetic_${p.id}`,
          user_id: p.id,
          full_name: p.full_name || p.name || 'Sem nome',
          email: p.email || '',
          plan_name: planInfo?.plan_name ?? (adminIds.has(p.id) ? 'Admin' : 'Sem plano'),
          login_at: p.last_seen_at,
          last_active_at: p.last_seen_at,
          device_type: null,
          browser_name: null,
          os_name: null,
          ip_address: null,
          session_label: null,
          is_admin: adminIds.has(p.id),
          is_synthetic: true,
        });
      });

      // Re-sort after adding synthetic entries
      loginLog.sort((a, b) => (b.login_at ?? '').localeCompare(a.login_at ?? ''));

      // ── Alerts ────────────────────────────────────────────────────────
      const totalUsers = allProfiles.length;
      const pctAccessed = pct(usersInPeriod.size, totalUsers);
      const pctInactive30d = pct(usersInactiveOver30d, totalUsers);
      const pctInactive7d = pct(usersInactiveOver7d, totalUsers);
      const alerts: AccessAlert[] = [];

      if (pctInactive30d > 50) {
        alerts.push({
          type: 'danger', icon: 'ri-user-unfollow-line',
          title: 'Alta inatividade da base',
          description: `${pctInactive30d}% dos usuários não acessam há mais de 30 dias. Risco de churn elevado.`,
          value: `${pctInactive30d}%`,
        });
      }
      if (pctInactive7d > 40 && pctInactive30d <= 50) {
        alerts.push({
          type: 'warning', icon: 'ri-time-line',
          title: 'Usuários sem acesso recente',
          description: `${usersInactiveOver7d} usuário${usersInactiveOver7d !== 1 ? 's' : ''} (${pctInactive7d}%) não acessam há mais de 7 dias.`,
          value: `${pctInactive7d}%`,
        });
      }
      if (pctAccessed < 30 && totalUsers > 5) {
        alerts.push({
          type: 'warning', icon: 'ri-focus-3-line',
          title: 'Uso concentrado em poucos usuários',
          description: `Apenas ${pctAccessed}% da base acessou no período — pode indicar baixa adoção ou retenção.`,
          value: `${pctAccessed}%`,
        });
      }
      if (newUsersFirstAccess > 0) {
        alerts.push({
          type: 'success', icon: 'ri-user-add-line',
          title: 'Novos usuários com primeiro acesso',
          description: `${newUsersFirstAccess} usuário${newUsersFirstAccess !== 1 ? 's' : ''} realizou seu primeiro acesso no período selecionado.`,
          value: String(newUsersFirstAccess),
        });
      }
      if (pctAccessed >= 60) {
        alerts.push({
          type: 'success', icon: 'ri-heart-pulse-line',
          title: 'Engajamento de acesso saudável',
          description: `${pctAccessed}% da base acessou no período — índice de adoção acima da média esperada.`,
          value: `${pctAccessed}%`,
        });
      }
      if (alerts.length === 0) {
        alerts.push({
          type: 'info', icon: 'ri-shield-check-line',
          title: 'Sem alertas críticos de acesso',
          description: 'Nenhuma anomalia de acesso identificada para o período selecionado.',
        });
      }

      setData({
        kpis, devices: { deviceDist, browserDist, osDist },
        dailyTrend, hourlyDist, userTable, alerts, plans, loginLog,
      });
    } catch (err) {
      console.error('[useAccessLogs] error:', err);
    } finally {
      setLoading(false);
    }
  }, [user, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { data, loading, refetch: fetchData };
};
